import React, { useRef, useState, useEffect } from "react";
import styles from "./ImageSearchModal.module.scss";
import { Upload, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useAPI } from "@/Hooks";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { IMAGE_SEARCH_STORAGE_KEY } from "./ImageSearchModal";

interface ImageSearchModalContentProps {
  onUploadEnd?: () => void;
  setLocalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUploading?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUploaded?: React.Dispatch<React.SetStateAction<boolean>>;
  isUploading?: boolean;
  isUploaded?: boolean;
}

const ImageSearchModalContent: React.FC<ImageSearchModalContentProps> = ({
  setLocalIsOpen,
  setIsUploading,
  setIsUploaded,
  isUploading: externalIsUploading,
  isUploaded: externalIsUploaded,
  onUploadEnd,
}) => {
  const api = useAPI();
  const [isDragging, setIsDragging] = useState(false);

  // Create local state if props aren't provided
  const [localIsUploading, setLocalIsUploading] = useState(false);
  const [localIsUploaded, setLocalIsUploaded] = useState(false);

  // Use either provided props or local state
  const isUploading =
    externalIsUploading !== undefined ? externalIsUploading : localIsUploading;
  const isUploaded =
    externalIsUploaded !== undefined ? externalIsUploaded : localIsUploaded;

  // Function to safely update uploading state
  const updateIsUploading = (value: boolean) => {
    if (setIsUploading) {
      setIsUploading(value);
    } else {
      setLocalIsUploading(value);
    }
  };

  // Function to safely update uploaded state
  const updateIsUploaded = (value: boolean) => {
    if (setIsUploaded) {
      setIsUploaded(value);
    } else {
      setLocalIsUploaded(value);
    }
  };

  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [imageResult, setImageResult] = useState<string | null>(null);

  const navigate = useNavigate();
  // Effect to animate progress bar
  useEffect(() => {
    let progressInterval: NodeJS.Timeout;

    if (isUploading && uploadProgress === 50) {
      // Simulate progress from 50% to 90% while waiting for response
      progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev < 90) {
            return prev + 1;
          }
          clearInterval(progressInterval);
          return prev;
        });
      }, 100);
    }

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isUploading, uploadProgress]);

  // Effect to handle completion and closing
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isUploaded && uploadedFile && imageResult) {
      // Show "Uploaded" state briefly, then close modal and navigate
      timeout = setTimeout(() => {
        setLocalIsOpen?.(false);
        onUploadEnd?.();
        navigate(`/image-search/${imageResult}`);
      }, 1500);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isUploaded, uploadedFile, imageResult, navigate]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith("image/")) {
        handleImageUpload(file);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleImageUpload(e.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (file: File) => {
    updateIsUploading(true);
    setUploadProgress(50);
    setUploadedFile(file);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await api.post("/v1/image-search/upload/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Assuming the response has an image field with the string result
      const result = response.data.image;
      setImageResult(result);
      console.log("Image upload successful:", result);

      // Convert the file to a data URL for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        // Save the image data URL to localStorage
        if (typeof reader.result === "string") {
          saveImageToLocalStorage(reader.result);
        }
      };
      reader.readAsDataURL(file);

      // Smoothly animate to 100%
      setUploadProgress(100);

      // Short delay before showing "Uploaded" state
      setTimeout(() => {
        updateIsUploading(false);
        updateIsUploaded(true);
      }, 400);
    } catch (error) {
      console.error("Error uploading image:", error);
      updateIsUploading(false);

      // Show error toast notification
      toast.error("Failed to upload image. Please try again.");

      // Reset the upload state
      setUploadProgress(0);
      setUploadedFile(null);
    }
  };

  // Save image to localStorage
  const saveImageToLocalStorage = (imageUrl: string) => {
    try {
      const imageSearchData = {
        image: imageUrl,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(
        IMAGE_SEARCH_STORAGE_KEY,
        JSON.stringify(imageSearchData),
      );
    } catch (error) {
      console.error("Error saving image search data to localStorage:", error);
    }
  };
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={styles.imageSearchModalContent}
    >
      <h2 className={styles.modalTitle}>Image search</h2>
      <p className={styles.modalDescription}>
        Find similar designs using an image.
      </p>

      <div className={styles.contentContainer}>
        {isUploaded ? (
          <motion.div
            className={styles.uploadedContainer}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.uploadedIcon}>
              <Check size={24} />
            </div>
            <p className={styles.uploadedText}>Uploaded</p>
          </motion.div>
        ) : isUploading ? (
          <div className={styles.uploadingContainer}>
            <div className={styles.progressBarContainer}>
              <motion.div
                className={styles.progressBar}
                style={{ width: `${uploadProgress}%` }}
                transition={{
                  type: "spring",
                  stiffness: 60,
                  damping: 20,
                }}
              ></motion.div>
            </div>
            <p className={styles.uploadingText}>Uploading image...</p>
          </div>
        ) : (
          <div className={styles.uploadInitialContainer}>
            <div
              className={`${styles.dropArea} ${isDragging ? styles.dragging : ""}`}
            >
              <div className={styles.uploadOption} onClick={handleUploadClick}>
                <div className={styles.uploadIcon}>
                  <Upload size={24} />
                </div>
                <span>Upload</span>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
              </div>
            </div>
            <p className={styles.dragDropText}>Drag & drop image.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageSearchModalContent;
